import React, { useState } from 'react';
import {
  ScreenContainer,
  FlowContainer,
  InputScreen,
  ProgressBar,
  LoadingScreen,
} from '@nerdwallet/react-flow';
import { Title, Box } from '@nerdwallet/currency';
import { useMutation } from '@nerdwallet/apollo';

import PropTypes from 'prop-types';
import {
  repeatable,
  stringInputSpec,
  stringSelectSpec,
  boolCheckboxSpec,
} from '../../lib/editing-configs';
import EditableFlowMapper from './flow-fields/flow-fields-mapper';
import { getPercentage } from './lib/percentage';
import styles from './editable-flow.module.less';
import * as queries from './configs/queries';

const EditableFlow = ({
  sections,
  submitText,
  continueText,
  showHeading,
  redirectUrl,
  query,
  submitLoaderTitle,
  submitLoaderSubtitle,
}) => {
  const [current, setCurrent] = useState(0);
  const [isSubmit, setIsSubmit] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({});

  const [submitFlow] = useMutation(queries[query]);

  const onChange = (key, value) => {
    const newForm = { ...form, [key]: value };
    const checkFields = sections[current]?.inputs.filter(
      (item) => !newForm[item.keyValue]
    );
    if (checkFields.length === 0) {
      setIsDisabled(false);
    }
    setForm(newForm);
  };

  const onPrimaryClick = () => {
    if (current + 1 === sections.length) {
      setIsSubmit(true);
    }
    if (current + 1 < sections.length) {
      setCurrent(current + 1);
      setIsDisabled(true);
      return;
    }
    setLoading(true);
    if (query) {
      submitFlow({
        variables: {
          form: JSON.stringify({ fields: form }),
        },
      }).then(() => {
        if (redirectUrl) {
          window.location.href = redirectUrl;
        } else {
          setCurrent(0);
          setIsSubmit(false);
          setLoading(false);
          setForm({});
        }
      });
    }
  };

  const onBackClick = () => {
    setIsDisabled(false);
    setCurrent(current - 1);
  };

  const redirectToBackPage = () => {
    window.history.back();
  };

  const percentageValue = getPercentage(current, sections.length);
  const ctaText = current + 1 === sections.length ? submitText : continueText;

  if (loading) {
    return (
      <Box className={styles.loadingContainer}>
        <LoadingScreen
          title={submitLoaderTitle}
          subtitle={submitLoaderSubtitle}
        />
      </Box>
    );
  }

  return (
    <Box className={styles.editableFlowContainer}>
      <FlowContainer>
        <ScreenContainer
          primaryCta={{
            onClick: onPrimaryClick,
            text: ctaText,
            isSubmit,
            isDisabled,
          }}
          showBack={showHeading && current !== 0}
          showClose={showHeading}
          onBack={onBackClick}
          onCancel={redirectToBackPage}
          tracking={{ formName: 'lpc-flow', fieldName: 'lpc-flow' }}
        >
          <ProgressBar
            percentageComplete={percentageValue}
            message={`Step ${current + 1} of ${sections.length} ${
              sections[current]?.stepMessage
                ? sections[current].stepMessage
                : ''
            }`}
            numCategories={sections.length}
          />
          {sections?.map((item, index) => {
            if (index === current) {
              return (
                <InputScreen
                  headline={<Title size="large">{item.title}</Title>}
                >
                  {item?.inputs?.map((input) => (
                    <EditableFlowMapper
                      onChange={onChange}
                      value={form[input.keyValue] || ''}
                      {...input}
                    />
                  ))}
                </InputScreen>
              );
            }
            return null;
          })}
        </ScreenContainer>
      </FlowContainer>
    </Box>
  );
};

EditableFlow.displayName = 'EditableFlow';

EditableFlow.propTypes = {
  submitText: PropTypes.string,
  continueText: PropTypes.string,
  showHeading: PropTypes.bool,
  submitLoaderTitle: PropTypes.string,
  submitLoaderSubtitle: PropTypes.string,
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      stepMessage: PropTypes.string,
      inputs: PropTypes.arrayOf(
        PropTypes.shape({
          type: PropTypes.string,
          keyValue: PropTypes.string,
          label: PropTypes.string,
          options: PropTypes.arrayOf(
            PropTypes.shape({
              label: PropTypes.string,
              value: PropTypes.string,
            })
          ),
        })
      ),
    })
  ),
  query: PropTypes.string,
  redirectUrl: PropTypes.string,
};

EditableFlow.defaultProps = {
  query: 'projectTedQuery',
  submitText: 'Submit',
  submitLoaderTitle: '',
  submitLoaderSubtitle: '',
  continueText: 'Continue',
  showHeading: false,
  sections: [
    {
      title: 'Title goes here',
      stepMessage: '[Placeholder message]',
      inputs: [
        {
          type: 'radio',
          keyValue: 'firstQuestion',
          label: 'this is a radio group',
          options: [
            {
              label: 'This is an option',
              value: 'test 1',
            },
            {
              label: 'This is another option',
              value: 'test 2',
            },
          ],
        },
      ],
    },
  ],
};

EditableFlow.getEditingConfig = () => [
  stringInputSpec('submitText', 'Submit Button Text'),
  stringInputSpec('continueText', 'Continue Button Text'),
  boolCheckboxSpec('showHeading', 'Show Heading Buttons'),
  stringInputSpec('redirectUrl', 'Url to redirect on submit'),
  stringInputSpec('submitLoaderTitle', 'Submit loading title message text'),
  stringInputSpec(
    'submitLoaderSubtitle',
    'Submit loading subtitle message text'
  ),
  stringSelectSpec('query', 'Select a Query to submit the flow data', [
    { label: 'Project Ted Query', value: 'projectTedQuery' },
  ]),
  repeatable('sections', 'sections', 'sections', () => {
    return [
      stringInputSpec('title', 'Title'),
      stringInputSpec('stepMessage', 'Progress Bar message'),
      repeatable('inputs', 'inputs', 'Inputs', () => {
        return [
          stringSelectSpec('type', 'Select a field type', [
            { label: 'Select an option', value: '' },
            { label: 'Radio Group', value: 'radio' },
            { label: 'Cards', value: 'cards' },
          ]),
          stringInputSpec('label', 'Input title'),
          stringInputSpec('keyValue', 'Input key'),
          repeatable('options', 'options', 'Options', () => {
            return [
              stringInputSpec('label', 'Label'),
              stringInputSpec('value', 'Value'),
            ];
          }),
        ];
      }),
    ];
  }),
];

export default EditableFlow;
